import { useState } from 'react'
import { toast } from 'sonner'
import { apis } from '@/modules/apiClients'
import { useTar } from '@/modules/i18n'

/**
 * Copies a person's invitation link to the clipboard. The token is NOT shipped with the people
 * directory (we don't want every invite token broadcast on login), so it is fetched on demand —
 * only when the partner actually clicks "copy invite link" — via a dedicated org-scoped endpoint.
 */
export const useCopyInviteLink = () => {
  const { t } = useTar()
  const [copyingPersonId, setCopyingPersonId] = useState<string | null>(null)

  const copyInviteLink = async (personId: string) => {
    setCopyingPersonId(personId)
    try {
      const response = await apis.userService.personsIdInvitationTokenGet(personId)
      const token = response.data?.invitationToken
      if (!token) {
        toast.error(t('partner.inviteLinkUnavailable'))
        return
      }
      const link = `${window.location.origin}/?token=${token}`
      try {
        await navigator.clipboard.writeText(link)
        toast.success(t('common.linkCopied'))
      } catch {
        toast.error(t('common.failedToCopyLink') + '\n' + link, { duration: 15000 })
      }
    } catch {
      toast.error(t('partner.inviteLinkUnavailable'))
    } finally {
      setCopyingPersonId(null)
    }
  }

  return { copyInviteLink, copyingPersonId }
}
